import { BackCta, Column, Icon, Link, Row, TagText, Text } from "@bridgesplit/ui";
import { LoopMetadataTag } from "@bridgesplit/abf-sdk";
import { doNothing, formatDate } from "@bridgesplit/utils";
import { LoopExpanded, MarketGuideMode, isPtToken } from "@bridgesplit/abf-react";
import { COPY, LOOPS_SLUG } from "app/constants";

import { useLoopContext } from "../LoopContext";
import { LoopImage, OpenHowItWorks } from "../../common";

export default function LoopDetailHeader() {
    const { loopExpanded } = useLoopContext();

    return (
        <Column spacing={2}>
            <Link to={LOOPS_SLUG}>
                <BackCta back={doNothing} backCta={`All ${COPY.LOOP_TERM_PLURAL}`} textVariant="body2" />
            </Link>

            <Row spaceBetween alignItems={"flex-start"}>
                <Column spacing={2}>
                    <Row spacing={2}>
                        <LoopImage offset={-0.25} loopExpanded={loopExpanded} badgeSize="sm" size="lg" />
                        <LoopName loopExpanded={loopExpanded} />
                    </Row>

                    <LoopTags loopExpanded={loopExpanded} />
                </Column>
                {loopExpanded && (
                    <OpenHowItWorks variant="outlined" mode={MarketGuideMode.Earn} loopExpanded={loopExpanded} />
                )}
            </Row>
        </Column>
    );
}

function LoopName({ loopExpanded }: { loopExpanded: LoopExpanded | undefined }) {
    if (!loopExpanded) return null;
    const depositSymbol = loopExpanded.collateralToken.symbol;
    const principalSymbol = loopExpanded.principalToken.symbol;
    return (
        <Row spacing={0.5}>
            <Text variant="h2">{depositSymbol}</Text>
            <Text variant="h2" color="disabled">
                {principalSymbol}
            </Text>
        </Row>
    );
}

function LoopTags({ loopExpanded }: { loopExpanded: LoopExpanded | undefined }) {
    if (!loopExpanded) return null;
    const baseTags = loopExpanded.loopVault.metadataTags ?? [];
    const primaryTag = baseTags.includes(LoopMetadataTag.New)
        ? "New"
        : loopExpanded.loopVault.featured
        ? "Featured"
        : undefined;
    const additionalTags = [
        ...(baseTags.includes(LoopMetadataTag.Leverage) ? ["Leverage"] : []),
        ...(loopExpanded.loopVault.protocols ?? [])
    ];
    const showMaturity = isPtToken(loopExpanded.collateralToken) && loopExpanded.loopVault.maturityDate;
    if (!primaryTag && additionalTags.length === 0 && !showMaturity) return null;

    return (
        <Row spacing={1}>
            {primaryTag && <LoopTag text={primaryTag} variant="primary" />}
            {additionalTags.map((tag, index) => (
                <LoopTag key={index} text={tag} />
            ))}
            {showMaturity && (
                <LoopTag
                    text={formatDate(loopExpanded.loopVault.maturityDate, "date", {
                        alwaysShowYear: true,
                        showTime: "never"
                    })}
                    tooltip={`${loopExpanded.collateralToken.symbol} can be redeemed for its full value on this maturity date.`}
                />
            )}
        </Row>
    );
}

function LoopTag({
    text,
    tooltip,
    variant = "default"
}: {
    text: string;
    tooltip?: string;
    variant?: "primary" | "default";
}) {
    return (
        <TagText
            variant="colored"
            color={variant === "primary" ? "secondary" : undefined}
            textVariant="body2"
            px={1.5}
            py={0.5}
            borderRadius={20}
            useTooltip={!!tooltip}
            helpText={tooltip}
        >
            {text}
            {tooltip && <Icon type="help" />}
        </TagText>
    );
}
